import { useState, useEffect } from 'react';
import { supabase } from './lib/supabase';

// Palette + font mirror the dashboard conventions (see COLORS in App.js).
const COLORS = {
  navy: '#0d1f4e',
  navyLight: '#152a63',
  green: '#0f7a62',
  greenBright: '#22c57a',
  bg: '#f5f7fb',
  white: '#ffffff',
  border: '#dbe0ed',
  textMuted: '#8a93b0',
  textBody: '#4a5578',
};
const FONT = 'Inter, sans-serif';

// Fixed weekday order for grouping and rendering.
const DAY_ORDER = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// "2026-03-23" -> "March 23, 2026". Parsed by hand so there's no timezone shift.
function formatDate(d) {
  if (!d || typeof d !== 'string') return '';
  const [y, m, day] = d.split('-').map(n => parseInt(n, 10));
  if (!y || !m || !day) return d;
  return `${MONTHS[m - 1]} ${day}, ${y}`;
}

// 24hr "HH:MM" -> 12hr "H:MM AM/PM". Mirrors the formatter in App.js.
// Defensive: returns '' for anything missing or malformed.
function formatTime(t) {
  if (!t || typeof t !== 'string' || !t.includes(':')) return '';
  const [hStr, mStr] = t.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (isNaN(h) || isNaN(m)) return '';
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

function formatShiftTime(shift) {
  const start = formatTime(shift.start_time);
  const end = formatTime(shift.end_time);
  if (start && end) return `${start} - ${end}`;
  return start || end || '';
}

// Plain-English text for known submit failure reasons. Anything unrecognised
// falls back to a generic message so an employee never sees a raw code.
const REASON_TEXT = {
  closed: 'This availability request has been closed. Please contact your manager.',
  not_sent: 'This availability request is not open yet. Please contact your manager.',
  expired: 'This link has expired. Please contact your manager.',
  invalid_token: "This link isn't valid. Please contact your manager.",
  already_built: 'The schedule has already been built, so availability can no longer be changed. Please contact your manager.',
};
const GENERIC_SUBMIT_ERROR = 'Something went wrong submitting your availability. Please try again, or contact your manager.';

function reasonToEnglish(res) {
  const key = res && (res.reason || res.code || res.error);
  if (key && REASON_TEXT[key]) return REASON_TEXT[key];
  return GENERIC_SUBMIT_ERROR;
}

// ── Shared layout ──
const screenStyle = {
  fontFamily: FONT,
  background: COLORS.bg,
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
  boxSizing: 'border-box',
};
const cardStyle = {
  background: COLORS.white,
  borderRadius: 16,
  padding: '32px 24px',
  width: '100%',
  boxShadow: '0 10px 40px rgba(13,31,78,0.10)',
};

// Full-screen centered card, used for loading / invalid / closed / confirmation.
function Message({ title, body, sub, positive }) {
  return (
    <div style={screenStyle}>
      <div style={{ ...cardStyle, textAlign: 'center', maxWidth: 440 }}>
        {positive && <div style={{ fontSize: 44, marginBottom: 8 }}>✅</div>}
        <h1 style={{ fontFamily: FONT, fontSize: 24, fontWeight: 800, color: COLORS.navy, margin: '0 0 10px', letterSpacing: '-0.02em' }}>{title}</h1>
        <p style={{ fontFamily: FONT, fontSize: 16, lineHeight: 1.5, color: COLORS.textBody, margin: 0 }}>{body}</p>
        {sub && <p style={{ fontFamily: FONT, fontSize: 14, lineHeight: 1.5, color: COLORS.textMuted, margin: '14px 0 0' }}>{sub}</p>}
      </div>
    </div>
  );
}

export default function AvailabilityScreen({ token }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState({}); // shift id -> bool
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Load the request for this token on mount / when the token changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: result, error } = await supabase.rpc('get_availability_request', { p_token: token });
      if (cancelled) return;
      if (error) {
        console.error('get_availability_request error:', error);
        setData(null); // handled as "not valid" below — no technical detail shown
      } else {
        setData(result);
        // Pre-check the shifts she selected in a prior submission for this run.
        const prior = Array.isArray(result && result.prior_submission) ? result.prior_submission : [];
        if (prior.length && Array.isArray(result.shifts)) {
          const windowsByDay = {};
          prior.forEach(entry => {
            if (entry && entry.day) windowsByDay[entry.day] = Array.isArray(entry.windows) ? entry.windows : [];
          });
          const preChecked = {};
          result.shifts.forEach(shift => {
            const windows = windowsByDay[shift.day];
            if (windows && windows.includes(`${shift.start_time}-${shift.end_time}`)) {
              preChecked[shift.id] = true;
            }
          });
          setChecked(preChecked);
        }
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  function toggle(id) {
    setChecked(prev => ({ ...prev, [id]: !prev[id] }));
  }

  // Only compute groups once we have data; safe for the render-state guards below.
  const shifts = data && Array.isArray(data.shifts) ? data.shifts : [];
  const shiftsByDay = DAY_ORDER
    .map(day => ({ day, shifts: shifts.filter(s => s.day === day) }))
    .filter(group => group.shifts.length > 0);

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError('');

    // Only include days with at least one checked shift.
    const payload = shiftsByDay
      .map(({ day, shifts: dayShifts }) => ({
        day,
        windows: dayShifts
          .filter(s => checked[s.id])
          .map(s => `${s.start_time}-${s.end_time}`),
      }))
      .filter(d => d.windows.length > 0);

    const { data: res, error } = await supabase.rpc('submit_availability', {
      p_token: token,
      p_days: payload,
    });

    if (error) {
      console.error('submit_availability error:', error);
      setSubmitError(GENERIC_SUBMIT_ERROR);
      setSubmitting(false);
      return;
    }
    if (res && res.ok) {
      setSubmitted(true);
    } else {
      setSubmitError(reasonToEnglish(res));
    }
    setSubmitting(false);
  }

  // ── Render states ──
  if (loading) {
    return (
      <div style={screenStyle}>
        <div style={{ fontFamily: FONT, color: COLORS.textMuted, fontSize: 16 }}>Loading...</div>
      </div>
    );
  }

  if (!data || data.valid === false) {
    return <Message title="Link not valid" body="This link isn't valid. Please contact your manager." />;
  }

  if (data.run_status !== 'sent') {
    return <Message title="Request closed" body="This availability request is closed. Please contact your manager." />;
  }

  if (submitted) {
    return (
      <Message
        positive
        title={`Thanks, ${data.employee_name}!`}
        body="Your availability has been submitted."
        sub="You can reopen this link to change your answer any time before the schedule is built."
      />
    );
  }

  // ── The form ──
  const alreadySubmitted = data.submitted_at != null;
  return (
    <div style={{ fontFamily: FONT, background: COLORS.bg, minHeight: '100vh', padding: '24px 16px 48px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>

        {/* Brand */}
        <div style={{ fontWeight: 800, fontSize: 22, letterSpacing: '-0.03em', color: COLORS.navy, textAlign: 'center', marginBottom: 20 }}>
          THRIVE<span style={{ color: COLORS.greenBright }}>AI</span>
        </div>

        {/* Greeting */}
        <div style={{ ...cardStyle, padding: 24, marginBottom: 20 }}>
          <h1 style={{ fontFamily: FONT, fontSize: 24, fontWeight: 800, color: COLORS.navy, margin: '0 0 6px', letterSpacing: '-0.02em' }}>
            Hi {data.employee_name}
          </h1>
          <p style={{ fontFamily: FONT, fontSize: 15, color: COLORS.textBody, margin: 0, lineHeight: 1.5 }}>
            {alreadySubmitted ? (
              <>
                You already submitted your availability for{' '}
                <strong style={{ color: COLORS.navy }}>{formatDate(data.cycle_start)} – {formatDate(data.cycle_end)}</strong>.
                {' '}Tweak anything that's changed, or resubmit as-is.
              </>
            ) : (
              <>
                Please tap every shift you're available to work for{' '}
                <strong style={{ color: COLORS.navy }}>{formatDate(data.cycle_start)} – {formatDate(data.cycle_end)}</strong>.
              </>
            )}
          </p>
          {data.run_label && (
            <p style={{ fontFamily: FONT, fontSize: 13, color: COLORS.textMuted, margin: '10px 0 0' }}>{data.run_label}</p>
          )}
        </div>

        {/* Days */}
        {shiftsByDay.length === 0 && (
          <div style={{ ...cardStyle, marginBottom: 16, textAlign: 'center', fontFamily: FONT, fontSize: 15, color: COLORS.textBody }}>
            There are no shifts to choose from in this cycle.
          </div>
        )}

        {shiftsByDay.map(({ day, shifts: dayShifts }) => (
          <div key={day} style={{ ...cardStyle, padding: 0, marginBottom: 16, overflow: 'hidden' }}>
            <div style={{ background: COLORS.navy, padding: '12px 20px' }}>
              <span style={{ fontFamily: FONT, color: '#fff', fontWeight: 700, fontSize: 16 }}>{day}</span>
            </div>
            {dayShifts.map(shift => {
              const isOn = !!checked[shift.id];
              return (
                <label
                  key={shift.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '16px 20px', cursor: 'pointer',
                    borderTop: `1px solid ${COLORS.border}`,
                    background: isOn ? '#f2fbf6' : '#fff',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isOn}
                    onChange={() => toggle(shift.id)}
                    style={{ width: 24, height: 24, accentColor: COLORS.green, flexShrink: 0, cursor: 'pointer' }}
                  />
                  <span style={{ fontFamily: FONT, fontSize: 17, fontWeight: 600, color: COLORS.navy }}>
                    {formatShiftTime(shift)}
                  </span>
                </label>
              );
            })}
          </div>
        ))}

        {submitError && (
          <div style={{ fontFamily: FONT, background: '#fee2e2', border: '1px solid #b91c1c', borderRadius: 10, padding: '12px 16px', margin: '4px 0 16px', color: '#b91c1c', fontSize: 15, fontWeight: 600, lineHeight: 1.4 }}>
            {submitError}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            width: '100%', marginTop: 8, padding: 16, borderRadius: 12, border: 'none',
            background: submitting ? COLORS.textMuted : `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.green})`,
            color: '#fff', fontFamily: FONT, fontSize: 17, fontWeight: 700,
            cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? 'Submitting...' : alreadySubmitted ? 'Update Availability' : 'Submit Availability'}
        </button>

        <p style={{ fontFamily: FONT, fontSize: 13, color: COLORS.textMuted, textAlign: 'center', margin: '16px 0 0', lineHeight: 1.5 }}>
          You can reopen this link to change your answer any time before the schedule is built.
        </p>
      </div>
    </div>
  );
}
